export interface ValidationError {
  field: string;
  message: string;
}

export interface TransformationResult<T> {
  value: T;
  validationErrors: ValidationError[];
  isValid: boolean;
}

export interface Appraisal {
  ActivityNumber: string;
  ExternalReferenceId: string;
  TimeStamp: string;
  Rowkey?: string;
  HasJobCenterInfo: boolean;
  Project?: Project;
  PropertyTypeAndTenancy: PropertyTypeAndTenancy;
  SubjectPropertyLocation: SubjectPropertyLocation;
  ParcelInformation: ParcelInformation;
  SiteInformation: SiteInformation;
  Ownership?: Ownership;
  SiteConditions?: SiteConditions;
  FloodZoneInformation: FloodZoneInformation;
  ImprovementInformation: ImprovementInformation;
  ImprovementInformationIndustrial: ImprovementInformationIndustrial;
  ImprovementInformationLiving: ImprovementInformationLiving;
  ImprovementInformationRetail: ImprovementInformationRetail;
  TenantInformation: TenantInformation;
  ConstructionDetails: ConstructionDetails;
  MechanicalDetails: MechanicalDetails;
  Parking?: Parking;
  Taxes: Taxes;
  Zoning?: Zoning;
  ConstructionCosts?: ConstructionCosts;
  Valuations?: Valuations;
  CashFlowModel?: CashFlowModel;
  Cashflow?: Cashflow;
  CashflowLiving?: CashflowLiving;
  CashflowRetail?: CashflowRetail;
  RevenueAndExpense?: RevenueAndExpense;
  Assumptions?: Assumptions;
  LeaseExpirations?: LeaseExpirations;
  LeasingAssumptions?: LeasingAssumptions;
  ContractMonthRent?: ContractMonthRent;
  ValuationSummary?: ValuationSummary;  
  ValuationComments?: ValuationComments;
  ReviewHistory: ApprovalStatus[];
  LatestReview?: ApprovalStatus;
  SubmittedBy: string;
  JobCenterActivity: JobCenterActivity;
  FileName: string;
  ComparableIds: string[];
}

// Property Type and Tenancy
export interface PropertyTypeAndTenancy {
  PropertyType: string;
  PropertySubtype: string;
  Tenancy: string;
}

// Subject Property Location
export interface SubjectPropertyLocation {
  PropertyId?: string;
  PropertyName: string;
  AddressLine1: string;
  City: string;
  County: string;
  State: string;
  ZipCode: string;
  Country: string;
  Region: string;
  MetropolitanStatisticalArea: string;
  Market: string;
  Submarket: string;
  StreetLocation: string;
  Latitude: string;
  Longitude: string;
}

// Parcel Information
export interface ParcelInformation {
  AssessorsParcelNumber: string;
}

// Site Information
export interface SiteInformation {
  Shape: string;
  Topography: string;
  Access: string;
  Visibility: string;
  Frontage: string;
  SiteImprovements: string;
  GroundLeases: string;
  GroundLeaseDescription: string;

  PrimaryLandMeasure: string;
  PrimaryLandValue: number;
  PrimaryLandExcess: number;
  PrimaryLandUnit: string;

  SecondaryLandMeasure?: string;
  SecondaryLandValue?: number;
  SecondaryLandExcess: number;
  SecondaryLandUnit?: string;

  Electic: Utility;
  Gas: Utility;
  Refuse: Utility;
  Sewer: Utility;
  Water: Utility;  
  Telephone: Utility;
}

export interface SiteConditions {
  SoilConditions: string;
  LandUseRestrictions: string;
  Wetlands: string;
  HazardousSubstances: string;
  OverallUtility: string;
  Summary: string;
}

// Flood Zone Information
export interface FloodZoneInformation {
  FloodMapNumber: string;
  FloodMapDate: string;
  FloodZone: string;
  FloodZoneComment: string;
}

// Improvement Information
export interface ImprovementInformation {
  PrimaryImprovementMeasure: string;
  PrimaryImprovementValue: number;
  PrimaryImprovementPriorValue: number;
  PrimaryImprovementUnit: string;

  SecondaryImprovementMeasure: string;
  SecondaryImprovementValue?: number;
  SecondaryImprovementPriorValue?: number;
  SecondaryImprovementUnit: string;

  TertiaryImprovementMeasure: string;
  TertiaryImprovementValue?: number;
  TertiaryImprovementPriorValue?: number;
  TertiaryImprovementUnit?: string;

  YearBuilt: string;
  YearRenovated: string;
  EffectiveAge: string;
  EconomicLife?: string;
  NumberOfStories: string;
  NumberOfBuildings: string;
  PropertyClass: string;
  BuildingCondition?: string;
  BuildingQuality?: string;  
  Occupancy: string;
  PriorOccupancy: string;
  Vacancy: string;
  PriorVacancy: string;
}

export interface ImprovementInformationIndustrial {
  ClearCeilingHeight: string;
  NumberOfDockHighDoors: string;
  NumberOfDriveInDoors: string;
  PercentOfficeSpace: string;
}

export interface ImprovementInformationLiving {
  NumberOfUnits: string;
  ProjectAmenities: string;
  UnitAmenities: string;
}

export interface ImprovementInformationRetail {
  NumberOfAnchors: string;
  TotalAnchorsArea: string;
  TotalAnchorsGrossLeasableAreaPercentage: string;
}

// Construction Details
export interface ConstructionDetails {
  BasicConstruction: string;
  ConstructionStatus?: string;
  ExteriorWalls: string;
  RoofType: string;
  RoofSurface: string;  
}

// Mechanical Details
export interface MechanicalDetails {
  HVAC: string;
  FireProtection: string;
  Security: string;
  NumberofPassengerElevators?: number;
  NumberofFreightElevators?: number;
}

// Parking  
export interface Parking {
  ParkingTypeOne: string;
  ParkingTypeOneNoOfSpaces: number;

  ParkingTypeTwo?: string;
  ParkingTypeTwoNoOfSpaces?: number;

  ParkingTypeThree?: string;
  ParkingTypeThreeNoOfSpaces?: number;
}

// Taxes
export interface Taxes {
  CurrentTaxYear: string;
  AssessmentRatio: string;
  EqualizationRate: string;
  AreTaxesCurrent: string;
  IsThereAGrievanceUnderway: string;
  TheSubjectsAssessmentAndTaxesAre: string;
  TotalTaxRate: string;
  TotalPropertyTaxes: string;
  MonthTaxesDueWithoutPenalty: string;
  PercentDiscountIfTaxBillPaidByDecember: string;
  TotalTaxExemptions?: number;
  TotalAssessedValue?: number;
  ImprovementsAssessedValue?: number;
  LandAssessedValue?: number;
  AssessorsMarketValue?: number;
}

//Zoning
export interface Zoning {
  CurrentZoning: string;
  IsCurrentUsePermitted: string;
  PermittedUses: string;
  ProhibitedUses: string;
}

//Construction Costs
export interface ConstructionCosts {
  SoftCosts?: number;
  FurnitureFixturesandEquipment?: number;
  SiteImprovementCost?: number;
  BuildingImprovementCosts?: number;
  TotalEntrepreneurialProfit?: number;
}

// Valuations
export interface Valuations {
  Currency: string;
  DateOfReport: string;
  DateOfInspection: string;
  FIRREA: string;
  IsUSPAPCompliant: string;
  Stabilized: string;
  ClientOwnership: string;
  PreviouslyAppraised: string;
  Partner: string;
}

// Cash flow
export interface CashFlowModel {
  CashFlowModelName: string;
  PortfolioName: string;
  ScenarioName: string;
  PropertyName: string;
}

export interface TenantInformation {
  LargestTenant: string;
  LargestTenantArea: string;
  AvgExpiries: string;  
  LeaseExpires: string;
  NumberOfTenants: string;
}

export interface Cashflow {
  ImpliedOverallRate?: ImpliedOverallRate;
  NetOperatingIncomeTotal?: NetOperatingIncomeTotal;
  Year1NOIPSF?: string;
  Walt?: string;
  PriorWalt?: string;     
  Downtime: string;     
  CollectionLoss: string;
  PriorCollectionLoss?: string; 
  Concessions: string;
  PriorConcessions?: string;
  NetCashFlowAmount?: number;
  RealEstateTaxGrowthRate?: number;
}

export interface ImpliedOverallRate {
  Year1: string;
  Year2: string;
  Year3: string;
  Year4: string;
  Year5: string;
  Year6: string;
  Year7: string;
  Year8: string;
  Year9: string;
  Year10: string;
  Year11: string;
}

export interface NetOperatingIncomeTotal {
  Year1: string;
  Year2: string;
  Year3: string;
  Year4: string;
  Year5: string;
  Year6: string;
  Year7: string;
  Year8: string;
  Year9: string;
  Year10: string;
  Year11: string;
}

export interface CashflowLiving {
  NonApartmentTotalPSF?: string;
}

export interface CashflowRetail {
  RollingTwelveMonthsSalesPSF?: string;
}

export interface Assumptions {
  MarketInflationYears1_5: string;
  PriorMarketInflationYears1_5: string; 
  ExpenseInflationYears1_5: string;
  PriorExpenseInflationYears1_5: string;  
}

export interface LeaseExpirations {
  FiveYearCumulativeExpirationsPSF?: string;
}

export interface ContractMonthRent {
  AverageContractRentPSF: string;
  PriorAverageContractRentPSF: string;   
  AnnualContractRentTotal: string;
  PriorAnnualContractRentTotal: string; 
  AverageMarketRentPSF: string;
  PriorAverageMarketRentPSF: string;    
  AnnualMarketRentTotal:string;
  CWProjectionMonthlyMarketRent: string;
  CRMR: string;
  MarketOccupancyPercent: string;
}

export interface ValuationSummary {
  scenarios: ValuationScenario[]
}

export interface ValuationScenario {
  IncludeScenario?: string;
  Scenario: string;
  InterestAppraised: string;
  ValueType: string;
  ValueCondition: string;
  ValueTiming: string;
  DateOfValue?: string;
  PriorDateOfValue: string;
  LandValue: string;
  ExcessLandValue: string;
  InsurableReplacementCost: string;
  CostApproachIndicatedValue?: string;
  TotalDepreciation?: number;
  SalesComparisonApproachIndicatedValue?: string;
  IncomeCapitalizationApproach: number;
  HoldingPeriod: number;
  PriorHoldingPeriod: number;  
  TerminalCapitalizationRate?: number;
  PriorTerminalCapitalizationRate?: number; 
  InternalRateOfReturn?: number;
  PriorInternalRateOfReturn?: number;     
  YieldCapitalizationIndicatedValue?: number;
  CapitalizationRate?: number;
  PriorCapitalizationRate?: number;     
  DirectCapitalizationIndicatedValue?: number;
  FinalValue: string;
  PriorFinalValue: string;
  FinalValuePerImprovementAreaMeasure: number;  
  PriorFinalValuePerImprovementAreaMeasure: number;  
  PrimaryValue: number;
  ExposurePeriod : string;
  MarketingPeriod : string;
}
export interface RevenueAndExpense {
  YearOne?: RevenueAndExpenseSection,
  StabilizedYear?: RevenueAndExpenseSection
}

export interface RevenueAndExpenseSection {
  BaseRentalRevenue?: string;
  TotalOtherIncome?: string;
  PotentialGrossRevenue?: string;
  VacancyandCollectionLoss?: string;
  EffectiveGrossRevenue?: string;
  GroundRent?: string;
  TotalExpenses?: string;
  NOITotalAmount?: string;
  OperatingExpenses?: string;
  GeneralnAdministrative?: string;
  Insurance?: string;
  ManagementFees?: string;
  RealEstateTaxes?: string;
  ReplacementReserves?: string;
}

export interface ValuationComments {
  AppraisalValueComments?: string;
}

// Approval Status Model
export interface ApprovalStatus {
  Id: string;
  AppraisalId: string;
  UserInfo: User;
  Status: ApprovalState;
  Comments: string;
  ActionDate: string;
  EntityType: string;
}

// Enum for Approval Status
export enum ApprovalState {
  PendingAppraiserReview = "PendingAppraiserReview",
  PendingPortfolioReview = "PendingPortfolioReview",
  PendingQualityReview = "PendingQualityReview",
  Approved = "Approved",
  Rejected = "Rejected"
}

export enum UserRoles {
  GlobalAdmin,
  PortfolioCoordinator,
  Appraiser,
  User,
  GlobalReader,
  Reader
}

export interface User {
  name: string;
  displayName: string;
  email: string;
  id: string;
  groups: { groupName: string; groupId: string; role: string }[];
  roles: string[];
  currentRole: string;
}

export interface Client {
  clientId: number;
  ClientName: string;      
  firstName: string;
  lastName: string;
  portfolios: Portfolio[];
}

export interface Portfolio {
  portfolioId: number;
  portfolioName: string;
  client?: Client;       
  projects: Project[];
}

export interface Project {
  projectId: string;
  projectName: string;
  projectNumber: string;
  portfolio?: Portfolio;
}

export enum LandAreaMeasure {
  GrossLandArea,
  NetLandArea
}

export enum ImprovementAreaMeasure {
  GrossBuildingArea,
  NetBuildingArea,
  NetRentableArea,
  GrossLeasableArea,
  ParkingSpace,
  TotalCenterGLA,
  UsableBuildingArea,
  Units,
  Beds,
  Holes,
  Pumps,
  Bushels,
  Rack
}

export enum UnitOfMeasure {
  Acres,
  Hectares,
  SquareFeet,
  SquareMeters,
  Kilowatts,
  Megawatts,
  Joules
}

export interface LookupItem {
  term: string;
  value: string;
  acronym: string;
}

export interface JobCenterActivity {
  activityNumber: string;
  statusType: string;
  activityStatusTypeID: string;
  firstName: string;
  lastName: string;
  clientName: string;
  clientID: string;
  portfolioID: string;
  portfolioName: string;
  addressLine1: string;
  appraisalDate?: string; 
  projectManager: string;
  bookedDate?: string;
  finalDueDate?: string;
  branchLegalName: string;
  projectID: string;
  projectNumber: string;
  propertyID: string;
}

export interface LeasingAssumptions {
  /** Lease Type (Reimbursements) */
  LeaseType: string;

  /** Market Lease Term */
  MarketLeaseTerm: string;

  /** Renewal Probability */
  RenewalProbability: string;

  /** Market Leasing Tenant Improvements New Leases */
  MarketLeasingTenantImprovementsNewLeases: string;

  /** Market Leasing Commissions New Leases */
  MarketLeasingCommissionsNewLeases: string;

  /** Year 1 Tenant Improvements */
  Year1TenantImprovements: string;

  /** Year 1 Leasing Commissions */
  Year1LeasingCommissions: string;

  /** Free Rent (Months) */
  FreeRent: string;

  /** Expense Reimbursement $/ SF */
  ExpenseReimbursement: string;
}

export interface Utility {
  PaidBy: string;
  Provider: string;
}

export interface GroupFieldConfig {
  label: string;         // label shown in the UI
  valueKey: string;      // key on ValuationScenario
  priorKey?: string;     // optional prior key on ValuationScenario
  typeKey: string;       // used for formatting (ties into valuationFormatTypes)
}

export type GroupItem = {
  label: string;
  current: string;
  prior: string;
  delta: string;
};

export interface Ownership {
  PropertyOwnershipEntity?: string;
  SoldInLastThreeYears?: boolean;
  SaleDate?: string;
  SalePrice?: number;
  ListPrice?: number;
  ContractPrice?: number;
}



